/**
 * A project file as an OPEN ITEM (RFC 0277 S6, S8).
 *
 * A file in the repository is not a document: it is in no catalog, and RFC
 * 0281 §4 is explicit that files never become documents. So, like a web page,
 * its id CARRIES what it is: `code:<ref>:<path>`, where an empty ref is the
 * working tree. Everything that resolves an open item takes an id and nothing
 * else (a `?file=` param after a refresh, the working set in localStorage), so
 * the id has to be enough to open it again.
 *
 * The ref is part of the id on purpose. `src/a.ts` on `main` and on a branch are
 * two different things to read, and one pane per ref is what lets them sit side
 * by side. Git forbids `:` in a ref name, so the first `:` after the prefix is
 * always the boundary, whatever the path contains.
 *
 * One ref is not a git ref: `~<branch>` (RFC 0329 3b) is that branch's
 * WORKING tree, read from the worktree that has it checked out, where a bare
 * branch is its last commit. Git forbids `~` in a ref name too, so the two can
 * never be confused.
 *
 * Nothing here is validated beyond shape: the git server vets the ref and the
 * path again on every read, which is the check that matters.
 */

import { mimeFromPath } from '@mod/core/model'
import type { Subject } from '../../shell/window/subject'

const PREFIX = 'code:'
const SOURCE_PREFIX = 'code-source:'

export const isCodeId = (id: string): boolean =>
  id.startsWith(PREFIX) || id.startsWith(SOURCE_PREFIX)

export function codeFileId(
  path: string,
  ref: string | null,
  sourceId?: string,
): string {
  return sourceId
    ? `${SOURCE_PREFIX}${encodeURIComponent(sourceId)}:${ref ?? ''}:${path}`
    : `${PREFIX}${ref ?? ''}:${path}`
}

/** The ref that reads `branch` as it is on disk in its worktree. */
export const worktreeRef = (branch: string): string => `~${branch}`

/** The branch a `~` ref names, or null for a plain ref or the working tree. */
export function worktreeBranch(ref: string | null): string | null {
  return ref?.startsWith('~') ? ref.slice(1) || null : null
}

/** The ref and path behind a code id, or null if it is not one. */
export function parseCodeId(
  id: string,
): { ref: string | null; path: string; sourceId: string } | null {
  if (!isCodeId(id)) return null
  const named = id.startsWith(SOURCE_PREFIX)
  let rest = id.slice(named ? SOURCE_PREFIX.length : PREFIX.length)
  let sourceId = 'default'
  if (named) {
    const end = rest.indexOf(':')
    if (end < 0) return null
    try {
      sourceId = decodeURIComponent(rest.slice(0, end))
    } catch {
      return null
    }
    rest = rest.slice(end + 1)
  }
  const colon = rest.indexOf(':')
  if (colon === -1) return null
  const path = rest.slice(colon + 1)
  if (!path) return null
  return { ref: rest.slice(0, colon) || null, path, sourceId }
}

/**
 * The open item for a project file.
 *
 * The title says the ref when there is one, because a pane showing a branch's
 * copy must never be mistaken for the file on disk. The mime comes from the
 * extension, the same rule a document is typed by, so a markdown file reads as
 * markdown and everything else as text.
 */
export function codeFileFor(
  path: string,
  ref: string | null,
  sourceId?: string,
): Subject {
  const name = path.slice(path.lastIndexOf('/') + 1)
  return {
    id: codeFileId(path, ref, sourceId),
    title: refTitle(name, ref),
    path,
    mime: mimeFromPath(path),
  }
}

function refTitle(name: string, ref: string | null): string {
  if (!ref) return name
  const branch = worktreeBranch(ref)
  return branch ? `${name} @ ${branch} (working)` : `${name} @ ${ref}`
}

export function codeFileFromId(id: string): Subject | null {
  const parsed = parseCodeId(id)
  return parsed ? codeFileFor(parsed.path, parsed.ref, parsed.sourceId) : null
}
